"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { axiosInstance } from "../../utility/apiInstance"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import CloseIcon from "@mui/icons-material/Close"
import VisibilityIcon from "@mui/icons-material/Visibility"
import toast from "react-hot-toast"
import { useSocket } from "../../context/SocketContext"

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
})

interface AWBData {
  sno: number
  awbNo: string
  origin: string
  destination: string
  flightDate: string
  _id?: string
  document_type?: string
  document_id?: string
  customer?: string
  organization?: string
  __v?: number
  response?: {
    id: string
    originalFileName: string
    fileFormat: string
    status: string
    response: Array<{
      document_type: string
      Document_no: string
      start_page_number: number
      end_page_number: number
      response: {
        awbNo: string
        flightNo: string
        flightDate: string
        airCarrier: string
        departureAirportCode: string
        destinationAirportCode: string
        shipperName: string
        shipperAddress: string
        consigneeName: string
        consigneeAddress: string
        notifyPartyName: string
        notifyPartyAddress: string
        deliveryAgentName: string
        deliveryAgentAddress: string
        deliveryAgentIataCode: string
        paymentTerms: string
        totalAmount: string
        totalAmountCurrency: string
        declaredValueForCarriage: string
        declaredValueForCustoms: string
        insuranceAmount: string | null
        details: Array<{
          noOfPackages: string
          noOfPackagesUom: string
          grossWeight: string
          grossWeightUom: string
          chargeableWeight: string
          chargeableWeightUom: string
          dimensionsValue: string[]
          dimensionsUnit: string
          rateClass: string
          rate: string
          amount: string
          amountCurrency: string
          productDescription: string
        }>
      }
    }>
    errorMessage: string | null
    remarks: string
    outputType: string[]
    totalPages: number
  }
}

interface DetailField {
  fieldName: string
  value: string
}

interface DimensionEntry {
  dimensions: string
  unit: string
}

interface DimensionObject {
  length: number
  width: number
  height: number
  quantity: number
  dimensionUnit: string
}

interface AWBDetails {
  basicDetails: DetailField[]
  stakeholders: DetailField[]
  commodityDetails: DetailField[]
  parsedDimensions: DimensionObject[]
}

const parseDimensionsData = (
  dimensionsValue: string[],
  dimensionsUnit: string,
): { formattedDimensions: DimensionEntry[]; parsedObjects: DimensionObject[] } => {
  const formattedDimensions: DimensionEntry[] = []
  const parsedObjects: DimensionObject[] = []

  dimensionsValue.forEach((dimension) => {
    const [dimensionStr, quantityStr] = dimension.split("_")
    const [lengthStr, widthStr, heightStr] = dimensionStr.split(/x/i)
    

    // Create DimensionEntry
    const dimensionsPart = `${lengthStr} × ${widthStr} × ${heightStr}`
    const formatted = {
      dimensions: `${dimensionsPart}_${quantityStr}`,
      unit: dimensionsUnit,
    }
    formattedDimensions.push(formatted)

    // Create DimensionObject with dimensionUnit
    const parsed = {
      length: Number(lengthStr),
      width: Number(widthStr),
      height: Number(heightStr),
      quantity: Number(quantityStr),
      dimensionUnit: dimensionsUnit,
    }
   
    parsedObjects.push(parsed)
  })

  return {
    formattedDimensions,
    parsedObjects,
  }
}

export default function Document_automation() {
  const [view, setView] = useState<"list" | "form" | "loading" | "details">("list")
  const [awbList, setAwbList] = useState<AWBData[]>([]);
  const [curStatus, setCurStatus] = useState<"INCOMPLETE" | "PROCESSING" | "COMPLETE" | "">("");
  const [remarks, setRemarks] = useState("");

  const [selectedFileType, setSelectedFileType] = useState("")
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [awbDetails, setAwbDetails] = useState<AWBDetails | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [selectedAwbId, setSelectedAwbId] = useState<string | null>(null)
  const [loadingContext, setLoadingContext] = useState<"upload" | "view" | null>(null)
  const socket = useSocket();
  const fileTypes = ["AWB"]

  const handleAddAWB = () => {
    setView("form")
    setSelectedFileType("")
    setUploadedFile(null)
    setAwbDetails(null)
    setSelectedAwbId(null)
  }

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    const fileSizeMB = selectedFile.size / 1024 / 1024
    if (fileSizeMB > 10) {
      toast.error("File size must not exceed 10 MB")
      return
    }

    setUploadedFile(selectedFile)
    setFileName(selectedFile.name)
  }, [])

  const handleRemoveFile = (clearFileType = true) => {
    setFileName(null)
    setUploadedFile(null)
    setAwbDetails(null)
    if (clearFileType) {
      setSelectedFileType("")
    }
  }

  const handleFetchDetails = async () => {

    setLoadingContext("upload")
    setView("loading")


    handleRemoveFile(false)

    if (uploadedFile && selectedFileType) {
      try {
        const formData = new FormData()
        formData.append("file", uploadedFile)
        formData.append("outputType", selectedFileType)

        const url = "/api/v1/automation/upload/awb"
        const response = await axiosInstance.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })


        if (response?.data?.statusCode === 200) {
          await handlegetAWBList()
        }
      } catch (err) {
        toast.error("File upload failed!")
      }
    }
  }

  const handleViewDetails = async (awbId: string) => {

    setSelectedAwbId(awbId)
    setLoadingContext("view")
    setView("loading")
    await fetchDetailsById(awbId, false)
  }

  const fetchDetailsById = async (documentId: string, waitFlag: boolean) => {
    const payload = {
      wait: waitFlag,
    }

    try {
      const response = await axiosInstance.post(
        `/api/v1/automation/details/${documentId}`,
        payload,
      )

      if (response?.data?.statusCode === 200) {
        const apiData = response.data.data

        if (!apiData || !apiData.response || !apiData.response.response || apiData.response.response.length === 0) {

          toast.error("No data found")
          setLoadingContext(null)
          setView("list")
          return

        }


        const awbResponse = apiData?.response?.response?.[0]?.response
        const details = awbResponse?.details?.[0] || {}

        let parsedDimensions: DimensionObject[] = []
        if (details.dimensionsValue && details.dimensionsUnit) {
          const { parsedObjects } = parseDimensionsData(details.dimensionsValue, details.dimensionsUnit)
          parsedDimensions = parsedObjects
        }
        const deliveryagentdata = awbResponse?.deliveryAgentName + "     " + awbResponse?.deliveryAgentIataCode

        const awbDetailsFromAPI: AWBDetails = {
          basicDetails: [
            { fieldName: "AWB Number", value: awbResponse?.awbNo || "N/A" },
            { fieldName: "Airline", value: awbResponse?.airCarrier || "N/A" },
            { fieldName: "Flight Number", value: awbResponse?.flightNo || "N/A" },
            { fieldName: "Flight Date", value: awbResponse?.flightDate || "N/A" },
            { fieldName: "Origin", value: awbResponse?.departureAirportCode || "N/A" },
            { fieldName: "Destination", value: awbResponse?.destinationAirportCode || "N/A" },
            { fieldName: "Payment Terms", value: awbResponse?.paymentTerms || "N/A" },
            {
              fieldName: "Total Amount",
              value: `${awbResponse?.totalAmount || "N/A"} ${awbResponse?.totalAmountCurrency || ""}`.trim(),
            },
            { fieldName: "Declared Value(Carriage)", value: awbResponse?.declaredValueForCarriage || "N/A" },
            { fieldName: "Declared Value(customs)", value: awbResponse?.declaredValueForCustoms || "N/A" },
            { fieldName: "Insurance Amount", value: awbResponse?.insuranceAmount || "N/A" },
          ],
          stakeholders: [
            { fieldName: "Shipper Name", value: awbResponse?.shipperName || "N/A" },
            { fieldName: "Shipper Address", value: awbResponse?.shipperAddress || "N/A" },
            { fieldName: "Consignee Name", value: awbResponse?.consigneeName || "N/A" },
            { fieldName: "Consignee Address", value: awbResponse?.consigneeAddress || "N/A" },
            { fieldName: "Notify Party", value: awbResponse?.notifyPartyName || "N/A" },
            { fieldName: "Notify Party Address", value: awbResponse?.notifyPartyAddress || "N/A" },
            { fieldName: "Delivery Agent", value: deliveryagentdata || "N/A" },
            { fieldName: "Delivery Agent Address", value: awbResponse?.deliveryAgentAddress || "N/A" },
          ],
          commodityDetails: [
            { fieldName: "Product Description", value: details?.productDescription || "N/A" },
            {
              fieldName: "Number Of Packages",
              value: `${details?.noOfPackages || "N/A"} ${details?.noOfPackagesUom || ""}`.trim(),
            },
            {
              fieldName: "Gross Weight",
              value: `${details?.grossWeight || "N/A"} ${details?.grossWeightUom || ""}`.trim(),
            },
            {
              fieldName: "Chargeable Weight",
              value: `${details?.chargeableWeight || "N/A"} ${details?.chargeableWeightUom || ""}`.trim(),
            },
            { fieldName: "Rate Class", value: details?.rateClass || "N/A" },
            { fieldName: "Rate", value: details?.rate || "N/A" },
            { fieldName: "Amount", value: `${details?.amount || "N/A"} ${details?.amountCurrency || ""}`.trim() },
          ],
          parsedDimensions: parsedDimensions,
        }

        setAwbDetails(awbDetailsFromAPI)
        setLoadingContext(null)
        setView("details")
      } else {
        toast.error("Try again after some time")
        setLoadingContext(null)
        setView("list")

      }
    } catch (err) {
      setLoadingContext(null)
      setView("list")

    }
  }
  const setDataOfAutomation = async (autmationData: any) => {

    try {
      const apiData = autmationData
      
      setCurStatus(""); setRemarks("");
      if (autmationData.status == "PROCESSING") {
        setView("loading");
        return;
      }
      if (autmationData.status == "INCOMPLETE") {
        setCurStatus("INCOMPLETE");
        setRemarks(autmationData.remarks);
        setView("details")
        return;
      }


      // Map API response to AWBDetails structure
      const awbResponse = apiData?.response?.[0]?.response
      const details = awbResponse?.details?.[0] || {}

      let parsedDimensions: DimensionObject[] = []
      if (details.dimensionsValue && details.dimensionsUnit) {
        const { parsedObjects } = parseDimensionsData(details.dimensionsValue, details.dimensionsUnit)
        parsedDimensions = parsedObjects
      }
      const deliveryagentdata = awbResponse?.deliveryAgentName + "     " + awbResponse?.deliveryAgentIataCode

      const awbDetailsFromAPI: AWBDetails = {
        basicDetails: [
          { fieldName: "AWB Number", value: awbResponse?.awbNo || "N/A" },
          { fieldName: "Airline", value: awbResponse?.airCarrier || "N/A" },
          { fieldName: "Flight Number", value: awbResponse?.flightNo || "N/A" },
          { fieldName: "Flight Date", value: awbResponse?.flightDate || "N/A" },
          { fieldName: "Origin", value: awbResponse?.departureAirportCode || "N/A" },
          { fieldName: "Destination", value: awbResponse?.destinationAirportCode || "N/A" },
          { fieldName: "Payment Terms", value: awbResponse?.paymentTerms || "N/A" },
          {
            fieldName: "Total Amount",
            value: `${awbResponse?.totalAmount || "N/A"} ${awbResponse?.totalAmountCurrency || ""}`.trim(),
          },
          { fieldName: "Declared Value(Carriage)", value: awbResponse?.declaredValueForCarriage || "N/A" },
          { fieldName: "Declared Value(customs)", value: awbResponse?.declaredValueForCustoms || "N/A" },
          { fieldName: "Insurance Amount", value: awbResponse?.insuranceAmount || "N/A" },
        ],
        stakeholders: [
          { fieldName: "Shipper Name", value: awbResponse?.shipperName || "N/A" },
          { fieldName: "Shipper Address", value: awbResponse?.shipperAddress || "N/A" },
          { fieldName: "Consignee Name", value: awbResponse?.consigneeName || "N/A" },
          { fieldName: "Consignee Address", value: awbResponse?.consigneeAddress || "N/A" },
          { fieldName: "Notify Party", value: awbResponse?.notifyPartyName || "N/A" },
          { fieldName: "Notify Party Address", value: awbResponse?.notifyPartyAddress || "N/A" },
          { fieldName: "Delivery Agent", value: deliveryagentdata || "N/A" },
          { fieldName: "Delivery Agent Address", value: awbResponse?.deliveryAgentAddress || "N/A" },
        ],
        commodityDetails: [
          { fieldName: "Product Description", value: details?.productDescription || "N/A" },
          {
            fieldName: "Number Of Packages",
            value: `${details?.noOfPackages || "N/A"} ${details?.noOfPackagesUom || ""}`.trim(),
          },
          {
            fieldName: "Gross Weight",
            value: `${details?.grossWeight || "N/A"} ${details?.grossWeightUom || ""}`.trim(),
          },
          {
            fieldName: "Chargeable Weight",
            value: `${details?.chargeableWeight || "N/A"} ${details?.chargeableWeightUom || ""}`.trim(),
          },
          { fieldName: "Rate Class", value: details?.rateClass || "N/A" },
          { fieldName: "Rate", value: details?.rate || "N/A" },
          { fieldName: "Amount", value: `${details?.amount || "N/A"} ${details?.amountCurrency || ""}`.trim() },
        ],
        parsedDimensions: parsedDimensions,
      }
      setAwbDetails(awbDetailsFromAPI)
      setLoadingContext(null)
      setView("details")

    } catch (err) {
      toast.error("Try again later")
      setLoadingContext(null)
      setView("form")

    }
  }

  const handlegetAWBList = async () => {
    try {
      const response = await axiosInstance.get("/api/v1/automation")


      if (response?.data?.statusCode === 200) {
        setAwbList(response?.data?.data)
      }
    } catch (error) {
    
    }
  }

  useEffect(() => {
    handlegetAWBList()
  }, [])


  useEffect(() => {
    if (!socket) return;

    socket.on("automationresponse", (autmationData) => {
      setDataOfAutomation(autmationData)
    });


    return () => {
      socket.off("automationresponse");
    };
  }, [socket]);

  const handleBackToList = () => {
    setView("list")
    setLoadingContext(null)
    handleRemoveFile()
    setAwbDetails(null)
    setSelectedAwbId(null)
  }

  const renderListView = () => (
    <Box sx={{ width: "100%", bgcolor: "#f0f2f5", minHeight: "100vh", p: 3 }}>
      {/* Add AWB Button Section */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            p: 4,
            borderRadius: "20px",
            backgroundColor: "#ffffff",
            boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Button
            variant="contained"
            sx={{
              bgcolor: "#F5AA06",
              color: "white",
              "&:hover": {
                bgcolor: "black",
              },
            }}
            onClick={handleAddAWB}
            size="large"
          >
            Add AWB
          </Button>
        </Paper>
      </Box>
      {/* Table Section */}
      <Box>
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            p: 4,
            borderRadius: "30px",
            backgroundColor: "#ffffff",
            boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <TableContainer component={Paper} sx={{ borderRadius: "20px" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>AWB No</TableCell>
                  <TableCell>Origin</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Flight Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {awbList.map((item, index) => {
                  const awbData = item?.response?.response?.[0]?.response
                  const displayData = {
                    sno: index + 1,
                    awbNo: awbData?.awbNo || "N/A",
                    origin: awbData?.departureAirportCode || "N/A",
                    destination: awbData?.destinationAirportCode || "N/A",
                    flightDate: awbData?.flightDate || "N/A",
                  }

                  return (
                    <TableRow key={item._id || index}>
                      <TableCell>{displayData.sno}</TableCell>
                      <TableCell>{displayData.awbNo}</TableCell>
                      <TableCell>{displayData.origin}</TableCell>
                      <TableCell>{displayData.destination}</TableCell>
                      <TableCell>{displayData.flightDate}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          sx={{
                            bgcolor: "#2196F3",
                            color: "white",
                            "&:hover": {
                              bgcolor: "#ffb310",
                            },
                          }}
                          onClick={() => handleViewDetails(item._id || "")}
                          disabled={!item._id}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  )

  const renderFormView = () => (
    <Box sx={{ width: "100%", bgcolor: "#f0f2f5", minHeight: "100vh", p: 3 }}>
      {/* Form Section */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            p: 4,
            borderRadius: "30px",
            backgroundColor: "#ffffff",
            boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Card sx={{ borderRadius: "20px" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Add New AWB
              </Typography>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Select File Type</InputLabel>
                    <Select
                      value={selectedFileType}
                      label="Select File Type"
                      onChange={(e) => setSelectedFileType(e.target.value)}
                    >
                      {fileTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth>
                    Upload File
                    <VisuallyHiddenInput
                      type="file"
                      onChange={handleFileUpload}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    />
                  </Button>
                  {fileName && (
                    <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                      <Typography variant="caption" display="block">
                        Selected: {fileName} ({uploadedFile ? (uploadedFile.size / 1024 / 1024).toFixed(2) : "0"} MB)
                      </Typography>
                      <IconButton size="small" onClick={() => handleRemoveFile()} sx={{ marginLeft: 1 }}>
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: "#F5AA06",
                      color: "white",
                      "&:hover": {
                        bgcolor: "black",
                      },
                    }}
                    onClick={handleFetchDetails}
                    disabled={!selectedFileType || !uploadedFile}
                    fullWidth
                  >
                    Fetch Details
                  </Button>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <Button variant="outlined" onClick={handleBackToList}>
                  Back to List
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      </Box>
      {/* Empty Results Section */}
      <Box>
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            p: 4,
            borderRadius: "30px",
            backgroundColor: "#ffffff",
            boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.1)",
            minHeight: "200px",
          }}
        >
          <Card sx={{ borderRadius: "20px", minHeight: "150px" }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="center" minHeight="150px">
                <Typography variant="body1" color="text.secondary">
                  {loadingContext === null && awbDetails === null
                    ? "Upload a file and click 'Fetch Details' to see the results here"
                    : "Please try uploading again"}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Box>
  )

  const renderLoadingView = () => (
    <Box sx={{ width: "100%", bgcolor: "#f0f2f5", minHeight: "100vh", p: 3 }}>
      {/* Conditionally show form section only for upload context */}
      {loadingContext === "upload" && (
        <Box sx={{ mb: 3 }}>
          <Paper
            elevation={3}
            sx={{
              width: "100%",
              p: 4,
              borderRadius: "30px",
              backgroundColor: "#ffffff",
              boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Card sx={{ borderRadius: "20px" }}>
              <CardContent>
              
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth disabled>
                      <InputLabel>Select File Type</InputLabel>
                      <Select value={selectedFileType} label="Select File Type">
                        {fileTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type.toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth disabled>
                      Upload File
                    </Button>
                    {fileName && (
                      <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                        <Typography variant="caption" display="block">
                          Selected: {fileName} ({uploadedFile ? (uploadedFile.size / 1024 / 1024).toFixed(2) : "0"} MB)
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: "#F5AA06",
                        color: "white",
                      }}
                      disabled
                      fullWidth
                    >
                      Processing...
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Paper>
        </Box>
      )}

      {/* Loading Results Section */}
      <Box>
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            p: 4,
            borderRadius: "30px",
            backgroundColor: "#ffffff",
            boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Card sx={{ borderRadius: "20px" }}>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  {loadingContext === "view" ? "Fetching AWB details..." : "Processing..."}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                  {loadingContext === "view"
                    ? "Please wait while we retrieve the details"
                    : "This may take a few moments"}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Box>
  )

  const renderDetailsView = () => (
    <Box sx={{ width: "100%", bgcolor: "#f0f2f5", minHeight: "100vh", p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            p: 4,
            borderRadius: "20px",
            backgroundColor: "#ffffff",
            boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h5" fontWeight="bold">
              AWB Details
            </Typography>
            <Button variant="outlined" onClick={handleBackToList}>
              Back to List
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Details Results Section */}
      <Box>
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            p: 4,
            borderRadius: "30px",
            backgroundColor: "#ffffff",
            boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Card sx={{ borderRadius: "20px" }}>
            <CardContent>
              {
                curStatus == "INCOMPLETE" ? <Box>

                  <Box sx={{ position: "relative", my: 4 }}>
                    <Divider />
                    <Typography
                      variant="h6"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "white",
                        px: 2,
                        fontWeight: "bold",
                        color: "primary.main",
                      }}
                    >
                      {remarks}
                    </Typography>
                  </Box>

                </Box> : null
              }

              {awbDetails && (
                <Box>
                  {/* Basic Details Section */}
                  <Box sx={{ position: "relative", my: 4 }}>
                    <Divider />
                    <Typography
                      variant="h6"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "white",
                        px: 2,
                        fontWeight: "bold",
                        color: "primary.main",
                      }}
                    >
                      Basic Details
                    </Typography>
                  </Box>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    {awbDetails.basicDetails.map((field, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {field.fieldName}
                        </Typography>
                        <TextField
                          fullWidth
                          value={field.value}
                          InputProps={{
                            readOnly: true,
                          }}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {/* Stakeholders Section */}
                  <Box sx={{ position: "relative", my: 4 }}>
                    <Divider />
                    <Typography
                      variant="h6"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "white",
                        px: 2,
                        fontWeight: "bold",
                        color: "primary.main",
                      }}
                    >
                      Stakeholders
                    </Typography>
                  </Box>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    {awbDetails.stakeholders.map((field, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {field.fieldName}
                        </Typography>
                        <TextField
                          fullWidth
                          value={field.value}
                          InputProps={{
                            readOnly: true,
                          }}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {/* Commodity Details Section */}
                  <Box sx={{ position: "relative", my: 4 }}>
                    <Divider />
                    <Typography
                      variant="h6"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "white",
                        px: 2,
                        fontWeight: "bold",
                        color: "primary.main",
                      }}
                    >
                      Commodity Details
                    </Typography>
                  </Box>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    {awbDetails.commodityDetails.map((field, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {field.fieldName}
                        </Typography>
                        <TextField
                          fullWidth
                          value={field.value}
                          InputProps={{
                            readOnly: true,
                          }}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {/* Dimensions Section */}
                  <Box sx={{ position: "relative", my: 4 }}>
                    <Divider />
                    <Typography
                      variant="h6"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "white",
                        px: 2,
                        fontWeight: "bold",
                        color: "primary.main",
                      }}
                    >
                      Dimensions
                    </Typography>
                  </Box>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    {awbDetails.parsedDimensions.map((dimension, index) => (
                      <Grid item xs={12} key={index}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Length
                            </Typography>
                            <TextField
                              fullWidth
                              value={dimension.length}
                              InputProps={{
                                readOnly: true,
                              }}
                              variant="outlined"
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Width
                            </Typography>
                            <TextField
                              fullWidth
                              value={dimension.width}
                              InputProps={{
                                readOnly: true,
                              }}
                              variant="outlined"
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Height
                            </Typography>
                            <TextField
                              fullWidth
                              value={dimension.height}
                              InputProps={{
                                readOnly: true,
                              }}
                              variant="outlined"
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Quantity
                            </Typography>
                            <TextField
                              fullWidth
                              value={dimension.quantity}
                              InputProps={{
                                readOnly: true,
                              }}
                              variant="outlined"
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Unit
                            </Typography>
                            <TextField
                              fullWidth
                              value={dimension.dimensionUnit}
                              InputProps={{
                                readOnly: true,
                              }}
                              variant="outlined"
                              size="small"
                            />
                          </Grid>

                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ width: "100%" }}>
      {view === "list" && renderListView()}
      {view === "form" && renderFormView()}
      {view === "loading" && renderLoadingView()}
      {view === "details" && renderDetailsView()}
    </Box>
  )
}
